// Non-recipe plan entries. A slot's PlannedMeal is either a recipe meal,
// a legacy free-text meal, or one of these entry meals (recipeId === 'entry').

export type MealEntry =
  | { type: 'dining'; place: string; note?: string }
  | { type: 'note'; text: string }
  | {
      type: 'leftover';
      refRecipeId: string;
      sub?: string;
      /**
       * Where the portion came from. Exactly one of these is set on entries
       * written after #663; both are absent on older ones, which is why the
       * remaining-count maths treats a missing link as "uncounted" rather than
       * assuming it drew on anything.
       */
      sourceMealId?: string;   // fridge: the cooked meal it was carved off
      pantryItemId?: string;   // freezer: the pantry row it was thawed from
      /** Portions this slot consumes. Absent means 1. */
      portions?: number;
      /** Frozen portions need taking out the night before; the card says so. */
      fromFreezer?: boolean;
    }
  | { type: 'skip' };

export type MealEntryType = MealEntry['type'];

/** Sentinel recipeId for entry meals — same pattern as 'free-text'. */
export const ENTRY_RECIPE_ID = 'entry';

interface HasEntry { recipeId: string; entry?: MealEntry }

export const isEntryMeal = (m: HasEntry): boolean =>
  m.recipeId === ENTRY_RECIPE_ID && !!m.entry;

/**
 * Can a dish be served beside this entry? A note is often the main itself —
 * meat bought at the butcher, a lasagna someone brings — and leftovers want a
 * fresh salad as much as a roast does. Resto and « rien » mean nothing is
 * cooked at home, so they keep the slot to themselves.
 */
export const entryTakesSides = (e: MealEntry): boolean =>
  e.type === 'note' || e.type === 'leftover';

/** An entry meal that is the main of its slot, with room for sides. */
export const isSideableEntryMeal = (m: HasEntry): boolean =>
  isEntryMeal(m) && entryTakesSides(m.entry!);

/** An entry meal that owns its slot alone (resto, « rien »). */
export const isExclusiveEntryMeal = (m: HasEntry): boolean =>
  isEntryMeal(m) && !entryTakesSides(m.entry!);

/** True only for meals whose recipeId can be resolved in the recipe index. */
export const isRecipeMeal = (m: HasEntry): boolean =>
  !isEntryMeal(m) && m.recipeId !== 'free-text';

/**
 * Loose shape so i18next's TFunction and a test stub both fit. These helpers
 * take the caller's `t` rather than reading i18n themselves: a module-level
 * lookup binds at import and goes stale after a language switch. Their
 * fallbacks used to be French literals, drawn on every entry card of the
 * English planner.
 */
export type EntryTranslate = (key: string, options?: Record<string, unknown>) => unknown;

/** resolveTitle: recipeId → title (or undefined if unresolvable). */
export function entryTitle(
  e: MealEntry,
  resolveTitle: (recipeId: string) => string | undefined,
  t: EntryTranslate,
): string {
  switch (e.type) {
    case 'dining': return e.place || String(t('entryForm.defaultPlace'));
    case 'leftover':
      return String(t('entry.leftoverTitle', {
        title: resolveTitle(e.refRecipeId) ?? String(t('entry.recentMeal')),
      }));
    case 'note': return e.text || String(t('entry.note'));
    case 'skip': return String(t('entry.nothingThatDay'));
  }
}

export function entrySub(e: MealEntry, t: EntryTranslate): string {
  switch (e.type) {
    case 'dining': return e.note || String(t('entry.diningLong'));
    case 'leftover': return e.sub || String(t('entry.subRecentMeal'));
    case 'note': return String(t('entry.noteFree'));
    case 'skip': return String(t('entry.subNoPlan'));
  }
}
